"use client";

import React from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import { GroqImage } from "@/helpers/Image";

import { PortableText } from "@portabletext/react";
import {
  customRichTextComponent,
  richTextComponent,
} from "@/sanity/sanityPortableText";

import { CTAButton } from "@/shadcn/ui/button";

import type { PageAffiliateSchema } from "@/sanity/pages/PageAffiliateTemplate";
import ChipSerif from "@/components/global/ui/ChipSerif";
import { PageTextOnlyLPSchema } from "@/sanity/pages/PageTextOnlyLPTemplate";

export default function Wins({ data }: { data: PageTextOnlyLPSchema }) {
  return (
    <section
      id="wins"
      className="w-full h-full lg:px-[7rem] lg:pr-[16rem] p-[4rem] lg:py-[6rem]"
    >
      <div className="relative mx-auto w-full">
        <div className="relative mx-auto w-[min(80.375rem,100%)] z-[1] flex flex-col items-start">
          {data?.winsChip && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              // className="bg-white py-[1.25rem] px-[1.5rem] rounded-full mb-[3rem] w-full lg:w-fit flex items-center justify-center"
            >
              <ChipSerif text={data.winsChip} />
            </motion.div>
          )}
          {data.winsTitle && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="richtext richtext-start prose-h2:font-medium prose-h2:text-[3rem] lg:prose-h2:text-[4rem] prose-h2:leading-[124%] prose-h2:tracking-[-0.09rem] lg:prose-h2:tracking-[-0.12rem] mb-[4rem] [&_br]:hidden lg:[&_br]:block"
            >
              <PortableText
                value={data.winsTitle}
                components={richTextComponent}
              />
            </motion.div>
          )}
          <div
            className={`relative w-full overflow-clip flex items-center h-[0.75rem] mb-[3.75rem]`}
          >
            <div className="absolute left-0 -translate-x-1/2 top-1/2 -translate-y-1/2 w-[0.75rem] h-[0.75rem] rotate-45 bg-[#DAE0DF]" />
            <div className="w-full bg-[#DAE0DF] h-[0.125rem]" />
            <div className="absolute right-0 translate-x-1/2 top-1/2 -translate-y-1/2 w-[0.75rem] h-[0.75rem] rotate-45 bg-[#DAE0DF]" />
          </div>
          {data.winsContent && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="richtext richtext-start prose-p:text-[2rem] lg:prose-p:text-[2rem] text-[#103037]/80
              prose-strong:text-[2.15rem] prose-strong:font-medium
              [&_.greenBlock]:w-full [&_.greenBlock]:mb-[3rem]
              [&_.squareTopicListNoDivisor]:w-full [&_.topicItem]:items-start [&_.topicItemSquare]:mt-[1.25rem]"
            >
              <PortableText
                value={data.winsContent}
                components={richTextComponent}
              />
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
